import { useSearchTenants } from '@/features/tenant'
import { AppBarProps, List } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserRoles } from '../../../../../features/auth'
import { TenantSearchFormContext } from '../../../../../features/tenant'
import routes from '../../../../../routes'
import { useTypedSelector } from '../../../../../store'
import SidebarSection from '../SidebarSectionLegacy'
import { TenantSearchItem } from '../TenantSearchItem/TenantSearchItemLegacy'
import NavBarMenuItem from './NavBarMenuItemLegacy'
import { useNotifications } from '@/hooks'

type NavBarProps = AppBarProps & {
  isOpen?: boolean
  handleForceOpen: () => void
  onNavigate?: () => void
}

type NavBarMenu = { icon: string; label: string; appendTenantSearch?: boolean }

type NavBarRoute = {
  header?: string
  authorize?: string | readonly string[]
  path: string
  menu?: NavBarMenu
  headerStyles?: React.CSSProperties
  showDivider?: boolean
  dividerStyles?: { mt?: number } & React.CSSProperties
}

const userIsAuthorize = (authorize: string | readonly string[] | undefined, roles: string[]) =>
  roles.some((role) => authorize?.includes(role))

export const NavBar = ({ isOpen, handleForceOpen, onNavigate }: NavBarProps) => {
  const { data: tenantsData } = useSearchTenants()
  const tenantsList = tenantsData ? (Array.isArray(tenantsData) ? tenantsData : tenantsData.items || []) : []
  const user = useTypedSelector((state) => state.authSlice.user)
  const tenant = useTypedSelector((state) => state.authSlice.tenant)

  const { t } = useTranslation('general')
  const [subdomain, setSubdomain] = useState<string>('')
  const rootUrl = new URL(document?.URL)

  const notifications = useNotifications(tenant?.id)

  useEffect(() => {
    const domain = new URL(rootUrl).hostname.replace('www.', '')
    const subdomain = domain.split('.')[0]
    setSubdomain(subdomain)
  }, [rootUrl])

  if (!user) {
    return <></>
  }

  return (
    <List>
      {routes.map((route, index: number) => {
        const { header, authorize, path, menu, headerStyles, showDivider, dividerStyles } = route as NavBarRoute
        const isAuthorized = userIsAuthorize(authorize, user.roles)
        let marginTop

        if (user.roles.includes(UserRoles.Participant)) {
          marginTop = 4
        } else if (user.roles.includes(UserRoles.CustomerSuccess)) {
          marginTop = 13
        } else if (
          user.roles.includes(UserRoles.CustomerManager) ||
          user.roles.includes(UserRoles.GlobalCustomerManager)
        ) {
          marginTop = 10
        } else {
          marginTop = 72
        }

        const adjustedDividerStyles = { ...dividerStyles, mt: dividerStyles?.mt == 0 ? 2 : marginTop }

        const notification = menu ? notifications[menu.label as keyof typeof notifications] : null

        const enhancedMenu =
          menu && notification
            ? {
                ...menu,
                badgeCount: notification.count,
                badgeTooltip: notification.tooltip,
              }
            : menu

        return (
          isAuthorized &&
          menu && (
            <TenantSearchFormContext key={index}>
              <SidebarSection
                header={header && t(header)}
                showDivider={showDivider}
                dividerStyles={adjustedDividerStyles}
                headerStyles={isOpen ? { ...headerStyles, display: 'block' } : { ...headerStyles, display: 'none' }}>
                {menu.appendTenantSearch ? (
                  <>
                    <NavBarMenuItem
                      isNavBarOpen={isOpen || false}
                      path={path}
                      menu={enhancedMenu ?? menu}
                      onNavigate={onNavigate}
                    />
                    {tenantsList && tenantsList.length > 0 && (
                      <TenantSearchItem
                        isNavBarOpen={isOpen || false}
                        subdomain={subdomain}
                        tenantsList={tenantsList}
                        handleForceOpen={handleForceOpen}
                      />
                    )}
                  </>
                ) : (
                  <NavBarMenuItem
                    isNavBarOpen={isOpen || false}
                    path={path}
                    menu={enhancedMenu ?? menu}
                    onNavigate={onNavigate}
                  />
                )}
              </SidebarSection>
            </TenantSearchFormContext>
          )
        )
      })}
    </List>
  )
}
